import { create } from "zustand";
import { invoke } from "@tauri-apps/api/core";
import { emit } from "@tauri-apps/api/event";

import { isTauri } from "@/lib/is-tauri";
import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";
import {
  appendInkPoint,
  storedInkPoints,
  type InkPoint,
} from "@/lib/sketch-stroke/user-ink-stroke";
import { subscribeDesktopChatAuthScope } from "./desktop-chat-auth-scope";
import { releaseScreenRecordingMedia } from "./screen-recording-media";

export type ScreenRecordingEvent = {
  phase: "starting" | "recording" | "processing" | "ready" | "error";
  attentionSessionId: string;
  clipId: string;
  displayId: number;
  startedMs: number;
  durationMs?: number;
  path?: string;
  frames?: Array<{ tMs: number; dataUrl: string; path?: string }>;
  screen?: ScreenshotScreenMeta;
  error?: string;
};

export type RecordingGesture = {
  recordingId: string;
  clipId?: string | null;
  strokeId: string;
  phase: "start" | "move" | "end";
  tMs: number;
  displayId: number | null;
  x: number;
  y: number;
};

export type RecordingStroke = {
  id: string;
  points: Array<InkPoint & { tMs: number }>;
  complete: boolean;
};

export type ScreenRecordingClip = ScreenRecordingEvent & {
  strokes: RecordingStroke[];
};

type RecordingState = {
  clips: Record<string, ScreenRecordingClip>;
  sessions: Record<string, "voice" | "text">;
  textScope: string | null;
  removedIds: string[];
};

const initial: RecordingState = {
  clips: {},
  sessions: {},
  textScope: null,
  removedIds: [],
};
export const useScreenRecordingStore = create<RecordingState>(() => initial);
const pendingGestures = new Map<string, RecordingGesture[]>();
const phaseOrder = {
  starting: 0,
  recording: 1,
  processing: 2,
  ready: 3,
  error: 3,
};

export function registerScreenRecordingSession(
  id: string,
  kind: "voice" | "text",
): void {
  useScreenRecordingStore.setState((state) => ({
    sessions: { ...state.sessions, [id]: kind },
  }));
}

export function setScreenRecordingTextScope(scope: string): void {
  if (useScreenRecordingStore.getState().textScope === scope) return;
  clearTextScreenRecordings(undefined, false);
  useScreenRecordingStore.setState({ textScope: scope });
}

export function screenRecordingTextSessionIds(): string[] {
  return Object.entries(useScreenRecordingStore.getState().sessions)
    .filter(([, kind]) => kind === "text")
    .map(([id]) => id);
}

export function clearTextScreenRecordings(
  preserveSessionId?: string,
  broadcast = true,
): void {
  const state = useScreenRecordingStore.getState();
  const textIds = new Set(screenRecordingTextSessionIds());
  for (const clip of Object.values(state.clips))
    if (textIds.has(clip.attentionSessionId))
      releaseScreenRecordingMedia(clip.clipId);
  useScreenRecordingStore.setState({
    clips: Object.fromEntries(
      Object.entries(state.clips).filter(
        ([, clip]) => !textIds.has(clip.attentionSessionId),
      ),
    ),
    sessions: Object.fromEntries(
      Object.entries(state.sessions).filter(
        ([id, kind]) => kind !== "text" || id === preserveSessionId,
      ),
    ),
    removedIds: [
      ...state.removedIds,
      ...Object.values(state.clips)
        .filter((clip) => textIds.has(clip.attentionSessionId))
        .map((clip) => clip.clipId),
    ].slice(-256),
  });
  if (broadcast) broadcastDraftAction({ action: "clear", preserveSessionId });
}

/** A dictated floating-composer draft retains its clips after the mic ends. */
export function adoptScreenRecordingSessionIntoText(
  id: string | null,
  broadcast = true,
): void {
  if (id) {
    registerScreenRecordingSession(id, "text");
    if (broadcast) broadcastDraftAction({ action: "adopt", sessionId: id });
  }
}

export function releaseScreenRecordingSession(id: string): void {
  const state = useScreenRecordingStore.getState();
  if (state.sessions[id] === "text") return;
  for (const clip of Object.values(state.clips))
    if (clip.attentionSessionId === id)
      releaseScreenRecordingMedia(clip.clipId);
  const sessions = { ...state.sessions };
  delete sessions[id];
  useScreenRecordingStore.setState({
    sessions,
    clips: Object.fromEntries(
      Object.entries(state.clips).filter(
        ([, clip]) => clip.attentionSessionId !== id,
      ),
    ),
  });
}

export function ingestScreenRecording(event: ScreenRecordingEvent): void {
  const state = useScreenRecordingStore.getState();
  if (
    !state.sessions[event.attentionSessionId] ||
    state.removedIds.includes(event.clipId)
  )
    return;
  const previous = state.clips[event.clipId];
  // An asynchronously delivered start must never resurrect a finalized clip.
  if (
    previous &&
    (phaseOrder[event.phase] < phaseOrder[previous.phase] ||
      (previous.phase === "ready" && event.phase !== "ready") ||
      (previous.phase === "error" && event.phase !== "error"))
  )
    return;
  useScreenRecordingStore.setState({
    clips: {
      ...state.clips,
      [event.clipId]: {
        ...previous,
        ...event,
        strokes: previous?.strokes ?? [],
      },
    },
  });
  const queued = pendingGestures.get(event.clipId) ?? [];
  pendingGestures.delete(event.clipId);
  if (event.phase !== "error")
    for (const gesture of queued) ingestScreenRecordingGesture(gesture);
}

export function isScreenRecordingSessionActive(id: string): boolean {
  return Object.values(useScreenRecordingStore.getState().clips).some(
    (clip) =>
      clip.attentionSessionId === id &&
      (clip.phase === "starting" ||
        clip.phase === "recording" ||
        clip.phase === "processing"),
  );
}

export function removeScreenRecordingClip(id: string, broadcast = true): void {
  releaseScreenRecordingMedia(id);
  const existed = !!useScreenRecordingStore.getState().clips[id];
  pendingGestures.delete(id);
  useScreenRecordingStore.setState((state) => {
    const clips = { ...state.clips };
    delete clips[id];
    return { clips, removedIds: [...state.removedIds, id].slice(-256) };
  });
  if (broadcast && existed && isTauri())
    void invoke("discard_screen_recordings", { clipIds: [id] }).catch(() => {});
  if (broadcast) broadcastDraftAction({ action: "remove", clipId: id });
}

/** A completed send owns only its prepared clips; later holds stay in draft. */
export function clearPreparedScreenRecordings(clipIds: string[]): void {
  for (const id of clipIds) removeScreenRecordingClip(id);
}

export function ingestScreenRecordingGesture(event: RecordingGesture): void {
  if (
    !event.clipId ||
    event.displayId === null ||
    !Number.isFinite(event.x) ||
    !Number.isFinite(event.y)
  )
    return;
  const state = useScreenRecordingStore.getState();
  const clip = state.clips[event.clipId];
  if (!clip) {
    if (
      !state.sessions[event.recordingId] ||
      state.removedIds.includes(event.clipId)
    )
      return;
    const queue = [...(pendingGestures.get(event.clipId) ?? []), event];
    pendingGestures.set(
      event.clipId,
      queue.length > 256 ? [queue[0]!, ...queue.slice(-255)] : queue,
    );
    if (pendingGestures.size > 16)
      pendingGestures.delete(pendingGestures.keys().next().value!);
    return;
  }
  if (
    clip.phase === "error" ||
    clip.attentionSessionId !== event.recordingId ||
    clip.displayId !== event.displayId
  )
    return;
  const strokes = [...clip.strokes];
  const index = strokes.findIndex((stroke) => stroke.id === event.strokeId);
  if (index < 0 && (event.phase !== "start" || strokes.length >= 64)) return;
  const stroke =
    index < 0
      ? { id: event.strokeId, points: [], complete: false }
      : strokes[index]!;
  const tMs = Math.max(0, event.tMs - clip.startedMs);
  let points = stroke.points;
  for (const point of storedInkPoints(
    points[points.length - 1] ?? null,
    { x: event.x, y: event.y, tMs },
    event.phase === "end",
  )) {
    points = appendInkPoint(points, { ...point, tMs });
  }
  const next = {
    ...stroke,
    points: points.slice(-2_000),
    complete: event.phase === "end",
  };
  if (index < 0) strokes.push(next);
  else strokes[index] = next;
  useScreenRecordingStore.setState({
    clips: { ...state.clips, [event.clipId]: { ...clip, strokes } },
  });
}

export const SCREEN_RECORDING_DRAFT_EVENT = "screen-recording-draft-action";
export type ScreenRecordingDraftAction = {
  action: "clear" | "remove" | "adopt";
  scopeKey?: string | null;
  clipId?: string;
  sessionId?: string;
  preserveSessionId?: string;
};

function broadcastDraftAction(action: ScreenRecordingDraftAction): void {
  void emit(SCREEN_RECORDING_DRAFT_EVENT, {
    ...action,
    scopeKey: useScreenRecordingStore.getState().textScope,
  }).catch(() => {});
}

export function applyScreenRecordingDraftAction(
  action: ScreenRecordingDraftAction,
): void {
  if (action.scopeKey !== useScreenRecordingStore.getState().textScope) return;
  if (action.action === "clear")
    clearTextScreenRecordings(action.preserveSessionId, false);
  if (action.action === "remove" && action.clipId)
    removeScreenRecordingClip(action.clipId, false);
  if (action.action === "adopt" && action.sessionId)
    adoptScreenRecordingSessionIntoText(action.sessionId, false);
}

subscribeDesktopChatAuthScope(() => {
  const attentionSessionIds = Object.keys(
    useScreenRecordingStore.getState().sessions,
  );
  pendingGestures.clear();
  useScreenRecordingStore.setState(initial);
  if (isTauri() && attentionSessionIds.length)
    void invoke("finish_screen_recordings", { attentionSessionIds }).catch(
      () => {},
    );
});
